import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/shared/page-header";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMe } from "@/hooks/use-auth";
import { useRiderStocks } from "@/hooks/use-rider-stocks";
import { useWorkspaces } from "@/hooks/use-workspaces";

export function RiderStockList() {
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);
  const [workspaceId, setWorkspaceId] = useState("");

  const {
    data: riderStocks = [],
    isLoading,
    refetch,
    isRefetching,
  } = useRiderStocks(workspaceId || undefined);

  const activeRidersWithStock = riderStocks.filter((r) =>
    r.items.some((it) => it.qty !== 0),
  );

  const totalCarriedQty = activeRidersWithStock.reduce(
    (sum, r) => sum + r.items.reduce((s, it) => s + it.qty, 0),
    0,
  );

  return (
    <div>
      <PageHeader
        title="Stok di Rider"
        description="Barang yang sudah diambil rider tapi belum disetor. Hilang dari sini setelah penjualan rider selesai."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
          >
            <RefreshCw
              className={`mr-1 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`}
            />
            Perbarui
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {me?.canSwitchWorkspace && (
          <div className="w-full sm:w-64">
            <SearchableSelect
              value={workspaceId}
              onChange={setWorkspaceId}
              options={workspaces.map((w) => ({ value: w.id, label: w.name }))}
              placeholder="Semua Workspace"
            />
          </div>
        )}
        {activeRidersWithStock.length > 0 && (
          <p className="text-sm text-muted-foreground">
            {activeRidersWithStock.length} rider membawa total {totalCarriedQty}{" "}
            unit produk
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Memuat data stok rider...
        </p>
      ) : activeRidersWithStock.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Semua rider telah selesai setoran atau belum ada pengeluaran barang.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {activeRidersWithStock.map((rider) => {
            const totalRiderUnits = rider.items.reduce(
              (s, it) => s + it.qty,
              0,
            );
            return (
              <div
                key={rider.riderId}
                className="flex flex-col gap-1.5 rounded-lg border bg-background p-3 shadow-xs"
              >
                <div className="flex items-center justify-between border-b pb-1">
                  <span className="text-sm font-medium text-foreground">
                    {rider.riderName}
                  </span>
                  <Badge
                    variant={totalRiderUnits < 0 ? "destructive" : "secondary"}
                    className="h-5 px-1.5 text-[11px]"
                  >
                    {totalRiderUnits} unit
                  </Badge>
                </div>
                <div className="flex flex-col gap-1 text-sm">
                  {rider.items
                    .filter((it) => it.qty !== 0)
                    .map((item) => (
                      <div
                        key={item.productId}
                        className="flex items-center justify-between text-muted-foreground"
                      >
                        <span className="truncate pr-2">
                          {item.productName}
                        </span>
                        <span
                          className={`font-mono font-medium ${
                            item.qty < 0
                              ? "text-destructive"
                              : "text-foreground"
                          }`}
                        >
                          {item.qty}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
